// src/services/QuickSetupService.js
// Orchestrates all API calls for the Quick Setup flow.

import api from '@/config/api'

/**
 * Parse the location's sign_up_context string into structured data.
 * The context is stored as a multi-line string during registration:
 *   Organization Name: ...
 *   Events/Services: ...
 *   Groups:
 *     3 Greeters
 *     2 Sound Team
 */
export function parseSignUpContext(contextString) {
  if (!contextString || typeof contextString !== 'string') {
    return { orgName: '', events: '', groups: [], volunteerCount: '', currentProcess: '', additionalInfo: '' }
  }

  const lines = contextString.split('\n')
  const result = { orgName: '', events: '', groups: [], volunteerCount: '', currentProcess: '', additionalInfo: '' }
  let inGroups = false

  for (const line of lines) {
    const trimmed = line.trim()
    if (trimmed.startsWith('Organization Name:')) {
      result.orgName = trimmed.replace('Organization Name:', '').trim()
      inGroups = false
    } else if (trimmed.startsWith('Events/Services:')) {
      result.events = trimmed.replace('Events/Services:', '').trim()
      inGroups = false
    } else if (trimmed.startsWith('Current Process:')) {
      result.currentProcess = trimmed.replace('Current Process:', '').trim()
      inGroups = false
    } else if (trimmed.startsWith('Volunteer Count:')) {
      result.volunteerCount = trimmed.replace('Volunteer Count:', '').trim()
      inGroups = false
    } else if (trimmed.startsWith('Additional Info:')) {
      result.additionalInfo = trimmed.replace('Additional Info:', '').trim()
      inGroups = false
    } else if (trimmed.startsWith('Groups:')) {
      inGroups = true
    } else if (inGroups && trimmed && trimmed !== 'N/A') {
      // Parse "3 Greeters" or "? Sound Team"
      const match = trimmed.match(/^(\d+|\?)\s+(.+)$/)
      if (match) {
        result.groups.push({
          name: match[2].trim(),
          count: match[1] === '?' ? 1 : parseInt(match[1], 10),
        })
      } else if (trimmed) {
        result.groups.push({ name: trimmed, count: 1 })
      }
    }
  }

  return result
}

/**
 * Derive a skill name from a workgroup name.
 * Removes common suffixes like "Team", "Group", "Ministry", "Crew".
 * De-pluralises simple cases.
 */
export function deriveSkillName(workgroupName) {
  if (!workgroupName) return ''
  let name = workgroupName.trim()

  // Remove common team/group suffixes
  name = name.replace(/\s+(team|group|ministry|crew|dept|department|squad|committee)$/i, '')

  // Simple de-pluralise (Greeters → Greeter, Ushers → Usher)
  if (name.length > 3 && name.endsWith('s') && !name.endsWith('ss')) {
    name = name.slice(0, -1)
  }

  return name.trim() || workgroupName.trim()
}

/**
 * Normalise a skill name for fuzzy comparison.
 * Strips spaces, hyphens, underscores, lowercases.
 * Handles common misspellings ("eazy" → "easy", doubled letters).
 */
export function normalizeForComparison(name) {
  if (!name) return ''
  let n = name.toLowerCase().trim()
  // Strip non-alphanumeric except letters/digits
  n = n.replace(/[\s\-_]+/g, '')
  // Common misspelling substitutions
  n = n.replace(/eazy/g, 'easy')
  n = n.replace(/worshipp/g, 'worship')
  // Remove trailing 's' for plural normalisation
  if (n.length > 3 && n.endsWith('s') && !n.endsWith('ss')) {
    n = n.slice(0, -1)
  }
  // Remove common suffixes
  n = n.replace(/(team|group|ministry|crew|dept|department|squad|committee)$/, '')
  return n
}

/**
 * Canonicalise an Activity name without removing meaningful service words.
 */
export function normalizeActivityName(name) {
  if (typeof name !== 'string') return ''
  return name
    .toLowerCase()
    .trim()
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

function activityNameSimilarity(left, right, minLength = 8) {
  if (!left || !right) return 0
  const maxLength = Math.max(left.length, right.length)
  if (maxLength < minLength) return 0

  let previousRow = Array.from({ length: right.length + 1 }, (_, index) => index)
  for (let leftIndex = 1; leftIndex <= left.length; leftIndex++) {
    const currentRow = [leftIndex]
    for (let rightIndex = 1; rightIndex <= right.length; rightIndex++) {
      const substitutionCost = left[leftIndex - 1] === right[rightIndex - 1] ? 0 : 1
      currentRow[rightIndex] = Math.min(
        currentRow[rightIndex - 1] + 1,
        previousRow[rightIndex] + 1,
        previousRow[rightIndex - 1] + substitutionCost
      )
    }
    previousRow = currentRow
  }

  return 1 - previousRow[right.length] / maxLength
}

/**
 * Return a small set of likely near-duplicates for an administrator to review.
 */
export function findPotentialActivityMatches(name, activities) {
  const normalizedName = normalizeActivityName(name)
  if (normalizedName.length < 8 || !Array.isArray(activities)) return []

  return activities
    .map((activity) => {
      const activityName = activity.activityname || activity.name || ''
      const normalizedActivityName = normalizeActivityName(activityName)
      return {
        activity,
        normalizedActivityName,
        similarity: activityNameSimilarity(normalizedName, normalizedActivityName),
      }
    })
    .filter(({ normalizedActivityName, similarity }) => (
      normalizedActivityName
      && normalizedActivityName !== normalizedName
      && similarity >= 0.84
    ))
    .sort((left, right) => right.similarity - left.similarity)
    .slice(0, 3)
    .map(({ activity }) => activity)
}

/**
 * Find the best matching existing skill for a given skill name.
 * Returns the existing skill object or null.
 */
export function findMatchingSkill(newSkillName, existingSkills) {
  if (!newSkillName || !existingSkills?.length) return null
  const normalised = normalizeForComparison(newSkillName)
  if (!normalised) return null

  // Exact match first (case-insensitive)
  const exact = existingSkills.find(
    s => (s.skillname || s.name || '').toLowerCase().trim() === newSkillName.toLowerCase().trim()
  )
  if (exact) return exact

  // Fuzzy match on normalised form
  const match = existingSkills.find(
    s => normalizeForComparison(s.skillname || s.name) === normalised
  )
  return match || null
}

/**
 * Find existing skills that look similar (but are not an exact normalised match)
 * to a proposed skill name, so the admin can choose to reuse one instead.
 */
export function findSimilarSkills(newSkillName, existingSkills) {
  if (!newSkillName || !Array.isArray(existingSkills) || !existingSkills.length) return []
  const target = normalizeForComparison(newSkillName)
  if (target.length < 4) return []

  return existingSkills
    .map((skill) => {
      const candidate = normalizeForComparison(skill.skillname || skill.name || '')
      if (!candidate || candidate === target) return null
      const similarity = activityNameSimilarity(target, candidate, 4)
      const contains = Math.min(target.length, candidate.length) >= 4
        && (candidate.includes(target) || target.includes(candidate))
      return similarity >= 0.75 || contains ? { skill, similarity: contains ? Math.max(similarity, 0.8) : similarity } : null
    })
    .filter(Boolean)
    .sort((a, b) => b.similarity - a.similarity)
    .slice(0, 2)
    .map(({ skill }) => skill)
}

/**
 * Given an array of workgroup names, produce a deduplicated skill map.
 * Returns { skillName: [workgroupIndex, ...] }
 */
export function buildSkillMap(workgroupNames) {
  const skillMap = {}
  workgroupNames.forEach((wgName, idx) => {
    const derived = deriveSkillName(wgName)
    const key = normalizeForComparison(derived)
    if (!key) return
    if (!skillMap[key]) {
      // Store with the proper casing from the first occurrence
      skillMap[key] = { displayName: derived, workgroupIndices: [] }
    }
    skillMap[key].workgroupIndices.push(idx)
  })
  return skillMap
}

/**
 * Map a day-of-week string to its JS Date.getDay() index (0=Sunday).
 */
const DAY_INDEX = {
  'Sunday': 0, 'Monday': 1, 'Tuesday': 2, 'Wednesday': 3,
  'Thursday': 4, 'Friday': 5, 'Saturday': 6,
}

const RECURRENCE_DAY_CHOICES = {
  'every Sunday': 'Sunday',
  'every Monday': 'Monday',
  'every Tuesday': 'Tuesday',
  'every Wednesday': 'Wednesday',
  'every Thursday': 'Thursday',
  'every Friday': 'Friday',
  'every Saturday': 'Saturday',
}

/**
 * Generate future dates that fall on `dayName` for the next `months` months.
 * Returns [{value: 'YYYY-MM-DD', label: 'Sun, Jan 5, 2025'}, ...]
 */
export function getRecurrenceStartDates(dayName, months = 3) {
  const target = DAY_INDEX[dayName]
  if (target === undefined) return []

  const dates = []
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const limit = new Date(today)
  limit.setMonth(limit.getMonth() + months)

  // Find the next occurrence of this day
  const cursor = new Date(today)
  const daysUntil = (target - cursor.getDay() + 7) % 7 || 7 // always future
  cursor.setDate(cursor.getDate() + daysUntil)

  while (cursor <= limit) {
    dates.push({
      value: formatDate(cursor),
      label: cursor.toLocaleDateString('en-US', {
        weekday: 'short', year: 'numeric', month: 'short', day: 'numeric'
      }),
    })
    cursor.setDate(cursor.getDate() + 7)
  }
  return dates
}

/**
 * Map a recurrence selection to the API recurrence parameters.
 * @param {string} recurrenceChoice - e.g. 'every Sunday'
 * @param {number} activityId
 * @param {string} recurStartDate - explicit start date (YYYY-MM-DD)
 */
export function mapRecurrenceToApi(recurrenceChoice, activityId, recurStartDate) {
  const day = RECURRENCE_DAY_CHOICES[recurrenceChoice]
  if (!day) return null // "single event" or "custom"

  const start = recurStartDate || formatDate(new Date())
  const startDt = new Date(start + 'T00:00:00')
  const endDt = new Date(startDt)
  endDt.setFullYear(endDt.getFullYear() + 1)

  return {
    activityid: activityId,
    recurrenceperiod: 'weekly',
    frequency: '1',
    daysofweek: [day],
    recurstartdate: start,
    recurenddate: formatDate(endDt),
    recurrencename: recurrenceChoice,
    recurrencedescription: `Repeats ${recurrenceChoice}`,
    cmd: 'addrecurrence',
  }
}

function formatDate(date) {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

function formatTime(hour, minute, meridiem) {
  let h = parseInt(hour, 10)
  if (meridiem === 'PM' && h !== 12) h += 12
  if (meridiem === 'AM' && h === 12) h = 0
  return `${String(h).padStart(2, '0')}:${String(minute).padStart(2, '0')}:00`
}

// ────────────────────────────────────────────
// API Methods
// ────────────────────────────────────────────

export async function fetchLocationSignUpContext(locationId) {
  try {
    const res = await api.post('/managelocationajax', {
      cmd: 'getlocationdetails',
      locationid: locationId,
    })
    return res.data?.output?.sign_up_context || ''
  } catch {
    return ''
  }
}

export async function fetchExistingSkills(locationId) {
  try {
    const res = await api.post('/managelocationajax', {
      cmd: 'getskills',
      locationid: locationId,
    })
    return res.data?.output || []
  } catch {
    return []
  }
}

export async function fetchExistingActivities(locationId) {
  const res = await api.get(`/activities/location/${locationId}?all=true`)
  if (res.data?.success !== true || !Array.isArray(res.data?.data)) {
    throw new Error('Could not verify existing activities. Please retry before creating an Activity.')
  }
  return res.data.data
}

export async function fetchLocationUsers(locationId) {
  try {
    const res = await api.post('/managelocationajax', {
      cmd: 'getusers',
      locationid: locationId,
    })
    return res.data?.output || []
  } catch {
    return []
  }
}

export async function createActivity(locationId, { name, description, startHour, startMinute, startMeridiem, duration }) {
  const time = formatTime(startHour, startMinute, startMeridiem)
  const res = await api.post('/manageactivityajax', {
    cmd: 'addnewactivity',
    locationid: locationId,
    activityname: name,
    description: description || '',
    starttime: time,
    duration: duration || 90,
  })
  if (!res.data?.success && !res.data?.id) {
    throw new Error(res.data?.message || 'Failed to create activity')
  }
  return res.data
}

export async function createSkill(locationId, skillName, description) {
  const res = await api.post('/manageskillajax', {
    cmd: 'addnewskill',
    locationid: locationId,
    skillname: skillName,
    skilldescription: description || '',
  })
  if (!res.data?.id && res.data?.returncode === '1') {
    throw new Error(res.data?.message || 'Failed to create skill')
  }
  return res.data
}

export async function createWorkgroup(activityId, { name, skillId, personsNeeded, autoSchedule, adminEmail }) {
  const res = await api.post('/manageactivityajax', {
    cmd: 'addworkgroup',
    activityid: activityId,
    workgroupname: name,
    description: '',
    workgroupskillid: skillId,
    personcount: personsNeeded || 1,
    addworkgroupenableautoschedule: autoSchedule ? 1 : 0,
    addworkgrouptoinstance: true,
    sendwgsms: 1,
    adminemail: adminEmail || '',
  })
  if (res.data?.returncode === 1 || (!res.data?.id && res.data?.returncode !== 0)) {
    throw new Error(res.data?.message || 'Failed to create workgroup')
  }
  return res.data
}

export async function createRecurrence(locationId, recurrenceParams) {
  const res = await api.post('/manageactivityajax', recurrenceParams)
  return res.data
}

export async function createSingleInstance(activityId, startTime, duration) {
  const today = new Date()
  // Create instance for the next upcoming relevant day
  const nextSunday = new Date(today)
  nextSunday.setDate(today.getDate() + ((7 - today.getDay()) % 7 || 7))
  const instanceDate = formatDate(nextSunday)

  const res = await api.post('/manageactivityajax', {
    cmd: 'addinstance',
    activityid: activityId,
    instancestartdate: instanceDate,
    instancestarttime: startTime,
    instanceduration: duration || 90,
    instanceautoschedule: false,
    instancestatus: 'active',
    instancenote: 'Created via Quick Setup',
  })
  return res.data
}

export async function saveActivityAdmins(activityId, personIds) {
  const res = await api.post('/manageactivityajax', {
    cmd: 'saveadmins',
    activityid: activityId,
    useridsarray: personIds,
  })
  return res.data
}

export async function saveWorkgroupAdmins(workgroupId, personIds) {
  const res = await api.post('/manageworkgroupajax', {
    cmd: 'saveadmins',
    workgroupid: workgroupId,
    useridsarray: personIds,
  })
  return res.data
}

export async function enableActivity(activityId) {
  const res = await api.post('/manageactivityajax', {
    cmd: 'saveactivitydetail',
    activityid: activityId,
    autoschedule: 1,
    autogenerate: 1,
    status: 'active',
  })
  return res.data
}

export async function sendQuickSetupCompletionEmail(activityId, locationId) {
  const res = await api.post('/quick-setup/completion-email', {
    activity_id: activityId,
    location_id: locationId,
  })
  return res.data
}

export async function fetchQuickSetupScheduleProgress(activityId) {
  const res = await api.get(`/quick-setup/activities/${activityId}/progress`)
  return res.data?.data || { status: 'error', upcoming_instance_count: 0 }
}

export async function fetchQuickSetupSkillAssignments(activityId) {
  const res = await api.get(`/quick-setup/activities/${activityId}/skills`)
  return res.data?.data || []
}

export async function fetchQuickSetupSkillEligibility(activityId) {
  const res = await api.get(`/quick-setup/activities/${activityId}/skill-eligibility`)
  return res.data?.data || []
}

export async function addQuickSetupPreferredPeople(activityId, personIds = null) {
  const payload = personIds ? { person_ids: personIds } : {}
  const res = await api.post(`/quick-setup/activities/${activityId}/preferred-people`, payload)
  return res.data?.data || {}
}

export async function runQuickSetupEngine(activityId, phase, instanceId = null) {
  const payload = { phase }
  if (instanceId !== null) payload.instance_id = instanceId
  const res = await api.post(`/quick-setup/activities/${activityId}/run-engine`, payload)
  return res.data?.data || {}
}

export async function fetchActivityWorkgroups(activityId) {
  try {
    const res = await api.post('/manageactivityajax', {
      cmd: 'getworkgroups',
      activityid: activityId,
    })
    return res.data?.output?.[0]?.workgroups || res.data?.output || []
  } catch {
    return []
  }
}
